import { useSyncExternalStore } from 'react'

// Lightweight toast/notification store.
// - info toasts auto-dismiss (used for the delete/undo affordance)
// - error toasts are PERSISTENT and must be dismissed explicitly. A failed
//   save is the worst failure this app can have, so it stays on screen until
//   the user acts — never a toast that silently disappears.

export interface ToastItem {
  id: number
  kind: 'info' | 'error'
  message: string
  actionLabel?: string
  onAction?: () => void
}

let items: ToastItem[] = []
let seq = 1
const listeners = new Set<() => void>()

function emit() {
  items = [...items]
  listeners.forEach((l) => l())
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function dismissToast(id: number) {
  items = items.filter((t) => t.id !== id)
  emit()
}

export function pushToast(
  message: string,
  opts?: { actionLabel?: string; onAction?: () => void; timeoutMs?: number },
): number {
  const id = seq++
  items = [...items, { id, kind: 'info', message, actionLabel: opts?.actionLabel, onAction: opts?.onAction }]
  emit()
  const ms = opts?.timeoutMs ?? 6000
  setTimeout(() => dismissToast(id), ms)
  return id
}

export function pushError(message: string): number {
  const id = seq++
  items = [...items, { id, kind: 'error', message }]
  emit()
  return id
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(subscribe, () => items)
}

export function Toaster() {
  const toasts = useToasts()
  if (toasts.length === 0) return null
  return (
    <div className="no-print fixed bottom-4 right-4 z-50 flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={
            'flex items-start gap-3 rounded-md px-3 py-2 text-sm shadow-lg ring-1 ' +
            (t.kind === 'error'
              ? 'bg-red-50 text-red-900 ring-red-300'
              : 'bg-slate-800 text-white ring-black/10')
          }
          role={t.kind === 'error' ? 'alert' : 'status'}
        >
          <span className="flex-1">{t.message}</span>
          {t.actionLabel && (
            <button
              className="font-semibold underline underline-offset-2"
              onClick={() => {
                t.onAction?.()
                dismissToast(t.id)
              }}
            >
              {t.actionLabel}
            </button>
          )}
          <button
            aria-label="Dismiss"
            className="opacity-60 hover:opacity-100"
            onClick={() => dismissToast(t.id)}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  )
}
